import React, { useState, useEffect, useMemo } from "react";
import "./../../../../../assets/css/Dashboard/ServicesKanban.scss";
import "./../../../../../assets/css/ClinicalCheckKanban/ClinicalCheckKanban.scss";
import useTitanApp from "../../../../../hooks/useTitanApp";
import { Skeleton } from "@mui/material";
import SortingServices from "../SortingServices";
import { Tag } from "primereact/tag";
import { useNavigate } from "react-router-dom";
import LoadingComponentCCKanban from "../../../../../components/LoadingComponentCCKanban/LoadingComponentCCKanban";
import { PrescriptionSourceFullTypes } from "../../../../../enum/prescriptionSourceFullTypes";
import { IoWarningOutline } from "react-icons/io5";
import PatientTag from "../PatientTag";
import CheckNameFilter from "../../../../../components/CheckFilter/CheckNameFilter";
import {
  getClinicalChecksTags,
  getKanbanCheck,
  getKanbanInbox,
  setKanbanFilters,
} from "../../../../../dispatchers/clinicalChecksDispatchers";
import LockedPrescriptionPopup from "../../../../../components/LockedPrescriptionPopup/LockedPrescriptionPopup";
import { lockedPrescriptions } from "../../../../../dispatchers/clinicalChecksDispatchers";
import { v4 as uuidv4 } from "uuid";
import ChecksFilter from "../../../../../components/CheckFilter/ChecksFilter";
import SortingMenu from "../../../../../components/SortingMenu/SortingMenu";
import RefreshBoard from "../../../../../components/CheckFilter/RefreshBoard";

const _dateFormatter = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

function ClinicalChecksKanban() {
  let navigate = useNavigate();
  const [lockedPrescription, setLockedPrescription] = useState(null);
  const [selectedPrescriptionId, setSelectedPrescriptionId] = useState(null);

  const {
    pharmacyServiceState,
    clincicalChecksState,
    appState,
    auth,
  } = useTitanApp();
  const { user } = auth || {};

  const { kanbanInbox, KanbanChecks, isCCKanbanLoading, kanbanFilters } =
    clincicalChecksState || {};
  const extractIncomingIds = (list) => {
    return list?.flatMap((item) => {
      if (
        item.grouped_PrescriptionIncomingIds &&
        item.grouped_PrescriptionIncomingIds.length > 0
      ) {
        return item.grouped_PrescriptionIncomingIds;
      } else if (item.prescriptionIncomingId) {
        return [item.prescriptionIncomingId];
      } else {
        return [];
      }
    });
  };

  const setKanbanIdsInStorage = (stages) => {
    const inboxItems =
      stages?.find((stage) => stage?.stageName === "Inbox")?.items || [];
    const checksItems =
      stages?.find((stage) => stage?.stageName === "Checks")?.items || [];
    const kanbanInboxIds = extractIncomingIds(inboxItems);
    const kanbanChecksIds = extractIncomingIds(checksItems);

    localStorage.setItem("kanbanInboxIds", JSON.stringify(kanbanInboxIds));
    localStorage.setItem("kanbanChecksIds", JSON.stringify(kanbanChecksIds));
  };
  const { darkMode, currentPharmacyId } = appState || {};
  const { scrollPosition } = pharmacyServiceState;

  const kanbanColumns = [
    { stageName: "Inbox", items: kanbanInbox },
    { stageName: "Checks", items: KanbanChecks },
  ];
  const [clinicalChecksData, setClinicalChecksData] = useState([]);
  const [baseClinicalChecksData, setBaseClinicalChecksData] = useState([]);

  useEffect(() => {
    if (
      kanbanFilters?.filterTagValuesCheck?.length > 0 ||
      kanbanFilters?.filterNMSValueDropdown
    ) {
      const filteredData = applyTagFilter(kanbanInbox, KanbanChecks);
      setClinicalChecksData(filteredData);
      setBaseClinicalChecksData(filteredData);
      setKanbanIdsInStorage(filteredData);
    } else {
      const originalData = [
        { stageName: "Inbox", items: kanbanInbox },
        { stageName: "Checks", items: KanbanChecks },
      ];
      setClinicalChecksData(originalData);
      setBaseClinicalChecksData(originalData);
      setKanbanIdsInStorage(originalData);
    }
  }, [kanbanFilters, kanbanInbox, KanbanChecks]);

  const movePrescriptionInList = (bucketStatus, prescriptionIncomingId) => {
    let updatedInbox = [...kanbanInbox];
    let updatedChecks = [...KanbanChecks];

    let foundIndexInbox = updatedInbox.findIndex(
      (item) => item?.prescriptionIncomingId === prescriptionIncomingId,
    );
    let foundIndexChecks = updatedChecks.findIndex(
      (item) => item?.prescriptionIncomingId === prescriptionIncomingId,
    );

    let foundItem = null;
    let isFromInbox = false;

    if (foundIndexInbox !== -1) {
      foundItem = updatedInbox.splice(foundIndexInbox, 1)[0];
      isFromInbox = true;
    } else if (foundIndexChecks !== -1) {
      foundItem = updatedChecks.splice(foundIndexChecks, 1)[0];
      isFromInbox = false;
    }

    if (foundItem) {
      if (isFromInbox) {
        if (bucketStatus === "top") {
          updatedInbox.unshift(foundItem);
        } else if (bucketStatus === "bottom") {
          updatedInbox.push(foundItem);
        }
      } else {
        if (bucketStatus === "top") {
          updatedChecks.unshift(foundItem);
        } else if (bucketStatus === "bottom") {
          updatedChecks.push(foundItem);
        }
      }
    }
    setClinicalChecksData([
      { stageName: "Inbox", items: updatedInbox },
      { stageName: "Checks", items: updatedChecks },
    ]);
  };

  const handleClick = async (id) => {
    setSelectedPrescriptionId(id);
    const prescriptionViewedBy = {
      viewedBy: user?.email,
      userId: uuidv4(),
      viewedTime: new Date().toISOString(),
      prescriptionIncomingIds: [id],
    };
    const response = await lockedPrescriptions({
      prescriptionViewedBy,
      currentPharmacyId,
    });

    if (response?.viewedBy !== null) {
      setLockedPrescription(response);
    } else {
      navigate(`/clinicalchecks/${currentPharmacyId}/${id}`, {
        state: { serviceType: "Checks" },
      });
      getClinicalChecksTags(currentPharmacyId).then((response) => {});
    }
  };

  const handleClosePopup = (navigateToSelected) => {
    setLockedPrescription(null);

    if (navigateToSelected && selectedPrescriptionId) {
      navigate(
        `/clinicalchecks/${currentPharmacyId}/${selectedPrescriptionId}`,
        {
          state: { serviceType: "Checks" },
        },
      );
    } else {
      navigate(`/pharmacy/clinicalchecks/${currentPharmacyId}`, {
        state: { serviceType: "Checks" },
        replace: true,
      });
    }
    getClinicalChecksTags(currentPharmacyId).then((response) => {});
    setSelectedPrescriptionId(null);
  };

  const formatDate = (isoString) => {
    const date = new Date(
      isoString.endsWith("Z") ? isoString : isoString + "Z",
    );
    const parts = _dateFormatter.formatToParts(date);
    const day = parts.find((p) => p.type === "day").value;
    const month = parts.find((p) => p.type === "month").value;
    const hours = parts.find((p) => p.type === "hour").value;
    const minutes = parts.find((p) => p.type === "minute").value;
    return `${day}/${month} (${hours}:${minutes})`;
  };

  const isKanbanColumnsEmpty = useMemo(
    () =>
      clinicalChecksData?.every((stage) => (stage.items?.length ?? 0) === 0),
    [clinicalChecksData],
  );

  const groupedStages = useMemo(
    () =>
      clinicalChecksData?.map((stage) => {
        const filteredItems =
          stage.stageName === "Inbox"
            ? (stage.items || []).filter(
                (item) => item.prescriptionSource !== 11,
              )
            : stage.items || [];

        const totalItemCount = filteredItems.reduce(
          (sum, item) => sum + (item?.prescriptionItemCount || 0),
          0,
        );

        const buckets = Object.entries(
          filteredItems.reduce((acc, item) => {
            if (!acc[item.bucket]) acc[item.bucket] = [];
            acc[item.bucket].push(item);
            return acc;
          }, {}),
        ).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));

        return { ...stage, filteredItems, totalItemCount, buckets };
      }),
    [clinicalChecksData],
  );
  const refreshBoardData = async () => {
    const inboxData = await getKanbanInbox(currentPharmacyId);
    const checksData = await getKanbanCheck(currentPharmacyId);
    const columns = [
      { stageName: "Inbox", items: inboxData },
      { stageName: "Checks", items: checksData },
    ];
    setClinicalChecksData(columns);
  };
  const filterTagsData = (filterTagValuesCheck, filterNMSValueDropdown) => {
    if (filterTagValuesCheck?.length > 0) {
      const tagSet = new Set(filterTagValuesCheck);
      const filteredData = clinicalChecksData
        .map((stage) => ({
          ...stage,
          items: stage.items.filter((item) => {
            const hasTag = item.patient.patientTags.some((tag) =>
              tagSet.has(tag.tagName),
            );
            return filterNMSValueDropdown === "Is" ? hasTag : !hasTag;
          }),
        }))
        .filter((stage) => stage.items.length > 0);
      setClinicalChecksData(filteredData);
    } else {
      setClinicalChecksData(kanbanColumns);
    }
  };

  const clearTagsFilter = () => {
    setKanbanFilters({
      filterNMSValueDropdown: "Is",
      filterTagValuesCheck: [],
    });
    setClinicalChecksData(kanbanColumns);
  };
  const applyTagFilter = (inbox, checks) => {
    const rawBoard = [
      { stageName: "Inbox", items: inbox },
      { stageName: "Checks", items: checks },
    ];

    const { filterTagValuesCheck = [], filterNMSValueDropdown } = kanbanFilters;

    if (filterTagValuesCheck.length === 0) return rawBoard;

    const tagSet = new Set(filterTagValuesCheck);
    return rawBoard
      .map((stage) => ({
        ...stage,
        items: stage.items.filter((item) => {
          const hasTag = item.patient.patientTags.some((tag) =>
            tagSet.has(tag.tagName),
          );
          return filterNMSValueDropdown === "Is" ? hasTag : !hasTag;
        }),
      }))
      .filter((stage) => stage.items.length > 0);
  };

  const filterPatientData = (patientName) => {
    if (patientName.trim() === "") {
      // reset to base filtered data (tag filters only)
      setClinicalChecksData(baseClinicalChecksData);
      return;
    }

    const search = patientName.toLowerCase();

    const filtered = baseClinicalChecksData
      .map((stage) => ({
        ...stage,
        items: stage.items.filter((item) => {
          const fullName = [
            item.patient?.title,
            item.patient?.forename,
            item.patient?.middleName,
            item.patient?.surname,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          return fullName.includes(search);
        }),
      }))
      .filter((stage) => stage.items.length > 0);

    setClinicalChecksData(filtered);
  };

  return (
    <>
      {lockedPrescription ? (
        <LockedPrescriptionPopup
          response={lockedPrescription}
          onClose={handleClosePopup}
        />
      ) : (
        <div className="pharmacy_header_modal">
          <div className="services_content-bg services_kanban services_cckanban">
            {isCCKanbanLoading ? (
              <LoadingComponentCCKanban />
            ) : isKanbanColumnsEmpty ? (
              <>
                <div className="search_nfilter">
                  <CheckNameFilter filterPatientData={filterPatientData} />
                  <div className="d-flex">
                    <RefreshBoard refreshBoardData={refreshBoardData} />
                    <ChecksFilter
                      filterTagsData={filterTagsData}
                      clearTagsFilter={clearTagsFilter}
                    />
                  </div>
                </div>
                <div className="pharmacy-error-message">
                  <p className="para_color">Data not available!</p>
                </div>
              </>
            ) : (
              <>
                {isCCKanbanLoading ? (
                  <div className="service_filter_skeleton">
                    <Skeleton
                      sx={{ bgcolor: darkMode ? "grey.900" : "" }}
                      height={80}
                      animation="wave"
                      variant="rounded"
                    />
                  </div>
                ) : (
                  <div className="search_nfilter">
                    <CheckNameFilter filterPatientData={filterPatientData} />
                    <div className="d-flex">
                      <RefreshBoard refreshBoardData={refreshBoardData} />
                      <ChecksFilter
                        filterTagsData={filterTagsData}
                        clearTagsFilter={clearTagsFilter}
                      />
                    </div>
                  </div>
                )}

                <div
                  id="stages-wrapper-id"
                  className="stages-wrapper cc-stages-wrapper"
                  style={{ overflow: "scroll", scrollLeft: scrollPosition }}
                >
                  {/* {clinicalChecksData?.map((stage) => {
                    let filteredItems = [];

                    if (stage?.stageName === "Inbox") {
                      filteredItems =
                        kanbanInbox?.filter(
                          (item) => item?.prescriptionSource !== 11,
                        ) || [];
                    } else if (stage?.stageName === "Checks") {
                      filteredItems = KanbanChecks || [];
                    } else {
                      filteredItems = stage?.items || [];
                    }

                    const totalItemCount = filteredItems?.reduce(
                      (sum, item) => sum + (item?.prescriptionItemCount || 0),
                      0,
                    ); */}
                  {groupedStages?.map((stage) => {
                    return (
                      <div id={"stage_" + stage?.stageName} className="stage">
                        <div className="stage_options">
                          <p className="service_title">
                            {isCCKanbanLoading ? (
                              <Skeleton
                                sx={{ bgcolor: darkMode ? "grey.900" : "" }}
                                width={100}
                                height={60}
                                animation="wave"
                              />
                            ) : (
                              `${stage?.stageName} (${stage.totalItemCount})`
                            )}
                          </p>

                          {stage?.sortedServices?.length > 0 ? (
                            <SortingServices stageType={stage?.stageType} />
                          ) : (
                            <></>
                          )}
                        </div>

                        <div className="data-box-container">
                          {stage.buckets.map(([bucket, items]) => (
                            <div key={bucket}>
                              <div className="bucket-heading-wrapper">
                                <div
                                  className="borders"
                                  style={{
                                    borderTop: `2px solid ${
                                      bucket === "1"
                                        ? "#ecaa00"
                                        : bucket === "2"
                                        ? "#00aaff"
                                        : bucket === "3"
                                        ? "#767676"
                                        : "#000"
                                    }`,
                                  }}
                                ></div>
                                <p className="text">
                                  {bucket === "1"
                                    ? "Waiting"
                                    : bucket === "2"
                                    ? "Today"
                                    : bucket === "3"
                                    ? "Unallocated"
                                    : ""}
                                </p>
                                <div
                                  className="borders"
                                  style={{
                                    borderTop: `2px solid ${
                                      bucket === "1"
                                        ? "#ecaa00"
                                        : bucket === "2"
                                        ? "#00aaff"
                                        : bucket === "3"
                                        ? "#767676"
                                        : "#000"
                                    }`,
                                  }}
                                ></div>
                              </div>

                              {items?.map((inboxdata, index) => (
                                <div key={index}>
                                  {inboxdata?.grouped_PrescriptionIncomingIds !==
                                    null &&
                                  inboxdata?.grouped_PrescriptionIncomingIds
                                    .length === 1 ? (
                                    <div className="check_service">
                                      {inboxdata?.awaitingQuery && (
                                        <div className="awaiting-query-box">
                                          <IoWarningOutline
                                            size={12}
                                            color={
                                              darkMode ? "#fff" : "#0a0a0a"
                                            }
                                          />
                                          <p>Awaiting query</p>
                                        </div>
                                      )}
                                      <div className="services_list">
                                        <div
                                          className="service_item"
                                          style={{
                                            borderLeft: `2px solid ${
                                              bucket === "1"
                                                ? "#ecaa00"
                                                : bucket === "2"
                                                ? "#00aaff"
                                                : bucket === "3"
                                                ? "#767676"
                                                : "#000"
                                            }`,
                                            borderRadius: `${
                                              inboxdata?.awaitingQuery
                                                ? "0px 16px 16px 16px"
                                                : "16px"
                                            }`,
                                          }}
                                          onClick={() =>
                                            handleClick(
                                              inboxdata?.prescriptionIncomingId,
                                            )
                                          }
                                        >
                                          <div className="services_left">
                                            <span
                                              className="left-tag"
                                              style={{
                                                border: `1px solid ${PrescriptionSourceFullTypes[
                                                  inboxdata?.prescriptionSource
                                                ]?.color || "#000"}`,
                                                color:
                                                  PrescriptionSourceFullTypes[
                                                    inboxdata
                                                      ?.prescriptionSource
                                                  ]?.color || "#000",
                                              }}
                                            >
                                              {PrescriptionSourceFullTypes[
                                                inboxdata?.prescriptionSource
                                              ]?.label || "Unknown"}
                                            </span>
                                          </div>

                                          <div className="content">
                                            <div className="Details-box">
                                              <div className="cc-kanban-details">
                                                <p className="services_kanban-serviceName">
                                                  {[
                                                    inboxdata?.patient?.title,
                                                    inboxdata?.patient
                                                      ?.forename,
                                                    inboxdata?.patient?.surname,
                                                  ]
                                                    .filter(Boolean)
                                                    .join(" ")}{" "}
                                                </p>

                                                <p className="address-details">
                                                  {[
                                                    inboxdata?.patient?.address
                                                      ?.houseName,
                                                    inboxdata?.patient?.address
                                                      ?.streetName,
                                                    inboxdata?.patient?.address
                                                      ?.postCode,
                                                  ]
                                                    .filter(Boolean)
                                                    .join(", ")}
                                                </p>
                                              </div>
                                              {inboxdata?.expiredItemCount >
                                                0 && (
                                                <Tag
                                                  severity="custom"
                                                  className="red-tag"
                                                  value="Expired"
                                                />
                                              )}
                                              <div className="cc-kanban-tags">
                                                {inboxdata?.patient?.patientTags?.map(
                                                  (tag, index) => (
                                                    <PatientTag
                                                      key={index}
                                                      tag={tag}
                                                      index={index}
                                                    />
                                                  ),
                                                )}
                                              </div>

                                              {inboxdata
                                                ?.prescriptionIncomingTags
                                                ?.length > 0 && (
                                                <div className="cc-kanban-tags">
                                                  {inboxdata.prescriptionIncomingTags?.map(
                                                    (tag) => (
                                                      <div className="pres-incoming-tag">
                                                        <div className="blinking-dot"></div>
                                                        <p>{tag?.tagName}</p>
                                                      </div>
                                                    ),
                                                  )}
                                                </div>
                                              )}

                                              <p className="comments">
                                                ({inboxdata?.commentCount})
                                                Comments
                                              </p>
                                            </div>
                                            <div className="cc-right-details">
                                              <div className="menu-tag">
                                                <Tag
                                                  className="color-tag"
                                                  value={
                                                    inboxdata ? (
                                                      <span className="tag-white-text">
                                                        {
                                                          inboxdata?.prescriptionItemCount
                                                        }{" "}
                                                        items
                                                      </span>
                                                    ) : (
                                                      ""
                                                    )
                                                  }
                                                />
                                                <SortingMenu
                                                  prescriptionIncomingId={
                                                    inboxdata?.prescriptionIncomingId
                                                  }
                                                  movePrescriptionInList={
                                                    movePrescriptionInList
                                                  }
                                                />
                                              </div>

                                              <p className="right-date">
                                                {formatDate(
                                                  inboxdata?.arrivalTime,
                                                )}
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="stacked-cards-container">
                                      <div className="check_service">
                                        {inboxdata?.awaitingQuery && (
                                          <div className="awaiting-query-box">
                                            <IoWarningOutline
                                              size={12}
                                              color={
                                                darkMode ? "#fff" : "#0a0a0a"
                                              }
                                            />
                                            <p>Awaiting query</p>
                                          </div>
                                        )}
                                        <div className="services_list">
                                          <div
                                            className="service_item"
                                            style={{
                                              borderLeft: `2px solid ${
                                                bucket === "1"
                                                  ? "#ecaa00"
                                                  : bucket === "2"
                                                  ? "#00aaff"
                                                  : bucket === "3"
                                                  ? "#767676"
                                                  : "#000"
                                              }`,
                                              borderRadius: `${
                                                inboxdata?.awaitingQuery
                                                  ? "0px 16px 16px 16px"
                                                  : "16px"
                                              }`,
                                            }}
                                            onClick={() =>
                                              handleClick(
                                                inboxdata?.prescriptionIncomingId,
                                              )
                                            }
                                          >
                                            <div className="services_left">
                                              <span
                                                className="left-tag"
                                                style={{
                                                  border: `1px solid ${PrescriptionSourceFullTypes[
                                                    inboxdata
                                                      ?.prescriptionSource
                                                  ]?.color || "#000"}`,
                                                  color:
                                                    PrescriptionSourceFullTypes[
                                                      inboxdata
                                                        ?.prescriptionSource
                                                    ]?.color || "#000",
                                                }}
                                              >
                                                {PrescriptionSourceFullTypes[
                                                  inboxdata?.prescriptionSource
                                                ]?.label || "Unknown"}
                                              </span>
                                            </div>

                                            <div className="content">
                                              <div className="Details-box">
                                                <div className="cc-kanban-details">
                                                  <p className="services_kanban-serviceName">
                                                    {[
                                                      inboxdata?.patient?.title,
                                                      inboxdata?.patient
                                                        ?.forename,
                                                      inboxdata?.patient
                                                        ?.surname,
                                                    ]
                                                      .filter(Boolean)
                                                      .join(" ")}{" "}
                                                  </p>

                                                  <p className="address-details">
                                                    {[
                                                      inboxdata?.patient
                                                        ?.address?.houseName,
                                                      inboxdata?.patient
                                                        ?.address?.streetName,
                                                      inboxdata?.patient
                                                        ?.address?.postCode,
                                                    ]
                                                      .filter(Boolean)
                                                      .join(", ")}
                                                  </p>
                                                </div>
                                                {inboxdata?.expiredItemCount >
                                                  0 && (
                                                  <Tag
                                                    severity="custom"
                                                    className="red-tag"
                                                    value="Expired"
                                                  />
                                                )}

                                                <div className="cc-kanban-tags">
                                                  {inboxdata?.patient?.patientTags?.map(
                                                    (tag, index) => (
                                                      <PatientTag
                                                        key={index}
                                                        tag={tag}
                                                        index={index}
                                                      />
                                                    ),
                                                  )}
                                                </div>

                                                {inboxdata
                                                  ?.prescriptionIncomingTags
                                                  ?.length > 0 && (
                                                  <div className="cc-kanban-tags">
                                                    {inboxdata?.prescriptionIncomingTags?.map(
                                                      (tag) => (
                                                        <div className="pres-incoming-tag">
                                                          <div className="blinking-dot"></div>
                                                          <p>{tag.tagName}</p>
                                                        </div>
                                                      ),
                                                    )}
                                                  </div>
                                                )}

                                                <p className="comments">
                                                  ({inboxdata?.commentCount})
                                                  Comments
                                                </p>
                                              </div>
                                              <div className="cc-right-details">
                                                <div className="menu-tag">
                                                  <Tag
                                                    className="color-tag"
                                                    value={
                                                      inboxdata ? (
                                                        <span className="tag-white-text">
                                                          {
                                                            inboxdata?.prescriptionItemCount
                                                          }{" "}
                                                          items
                                                        </span>
                                                      ) : (
                                                        ""
                                                      )
                                                    }
                                                  />
                                                  <SortingMenu
                                                    prescriptionIncomingId={
                                                      inboxdata?.prescriptionIncomingId
                                                    }
                                                    movePrescriptionInList={
                                                      movePrescriptionInList
                                                    }
                                                  />
                                                </div>
                                                <p className="right-date">
                                                  {formatDate(
                                                    inboxdata?.arrivalTime,
                                                  )}
                                                </p>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="bottom-stacked"></div>
                                      <div className="bottom-stacked"></div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
export default React.memo(ClinicalChecksKanban);
